import { useMemo, useState } from 'react'

import type { CivilDate } from '~/models/civilDate'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import { expiryStatus } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

export type ExpiryFilterResult<T> = {
  /** includeExpired が false のとき 'expired' の行を除いたもの。 */
  visibleRows: T[]
  includeExpired: boolean
  setIncludeExpired: (v: boolean) => void
  today: CivilDate
  /** includeExpired が false のときに隠している 'expired' の件数 (AC-09)。 */
  hiddenCount: number
  /**
   * 'expired' の行の件数。 includeExpired に関わらず数える (トグルのラベルの件数。
   * 押しても件数が変わらないように、 隠しているかどうかでなく終了した行の数を出す)。
   */
  expiredCount: number
}

/**
 * 失効表示トグルの状態と、それに応じたフィルタ済み行を提供する共通 hook
 * (occto-master-valid-period-6tabs 設計書 §5.4)。
 *
 * 各タブの一覧は、この hook の `visibleRows` を既存の
 * `filterRows(rows, selectedAreaCode, searchText)` に渡す 2 段構成にする
 * (検索・エリアの既存フィルタ構造には手を入れない)。
 */
export function useExpiryFilter<T>(
  rows: readonly T[],
  getPeriod: (row: T) => {
    validFrom: CivilDate
    validTo: CivilDate | undefined
  },
  // 失効フィルタを持たないタブの合成 hook (`useMasterCrudTab`) が
  // rules-of-hooks 回避のため常に呼ぶ経路では today の 1 分 interval が不要 (=
  // visibleRows が毎分同一内容で新配列生成され下流テーブルが無駄に再描画される
  // regression 回避)。既定 true で他 caller の挙動は変えない。
  enabled = true,
): ExpiryFilterResult<T> {
  const today = useTodayJst(enabled)
  const [includeExpired, setIncludeExpired] = useState(false)

  const { visibleRows, hiddenCount, expiredCount } = useMemo(() => {
    let hidden = 0
    let expired = 0
    const visible: T[] = []
    for (const row of rows) {
      const period = getPeriod(row)
      const status = expiryStatus(period.validFrom, period.validTo, today)
      if (status === 'expired') {
        expired += 1
        if (!includeExpired) {
          hidden += 1
          continue
        }
      }
      visible.push(row)
    }
    return { visibleRows: visible, hiddenCount: hidden, expiredCount: expired }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getPeriod is a per-render closure supplied by callers; row identity + today + includeExpired fully determine the result.
  }, [rows, today, includeExpired])

  return {
    visibleRows,
    includeExpired,
    setIncludeExpired,
    today,
    hiddenCount,
    expiredCount,
  }
}
